// components/ui/calendar.tsx
"use client";

import * as React from "react";
import { DayPicker } from "react-day-picker";
import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * 🔴 RYTHME À DEUX PALIERS, sur la HAUTEUR de la fenêtre.
 *
 * Ce calendrier mesurait 460 px à lui seul (+ ~70 px avec le champ « Heure »),
 * soit 538 px — pour 421 px réellement disponibles sur un écran de 551 px de
 * haut. Le popover a beau être borné depuis (cf. `popover.tsx`), **borner le
 * contenant ne rend pas son contenu compressible** : sans les paliers
 * ci-dessous on ne ferait que remplacer « ça sort de l'écran » par « ça défile
 * dans une lucarne de 421 px », c'est-à-dire un calendrier qu'on ne voit jamais
 * en entier. Même leçon que le clavier du dialer (2026-08-14).
 *
 * Les variantes `[@media(min-height:700px)]` rendent le calendrier d'avant **à
 * l'identique** au-dessus de 700 px de fenêtre — seuil non arbitraire : c'est
 * la hauteur à partir de laquelle Radix annonce assez de place (~570 px) pour
 * la version pleine taille. En dessous, la version compacte fait ~382 px, donc
 * elle tient entière sans défilement sur l'écran signalé (713×551).
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-0", className)}
      classNames={{
        months: "flex flex-col",
        month: "space-y-0",

        // Header mois/année
        month_caption:
          "bg-neutral-100 border-b px-4 pt-3 pb-2.5 flex items-center justify-center [@media(min-height:700px)]:px-6 [@media(min-height:700px)]:pt-5 [@media(min-height:700px)]:pb-4",
        caption_label: "hidden",
        dropdowns: "w-full flex items-center justify-center gap-6 [@media(min-height:700px)]:gap-14",

        months_dropdown:
          "capitalize bg-transparent border-0 text-base font-semibold leading-none focus:outline-none focus:ring-0 [@media(min-height:700px)]:text-lg",
        years_dropdown:
          "bg-transparent border-0 text-base font-semibold leading-none focus:outline-none focus:ring-0 [@media(min-height:700px)]:text-lg",

        // On cache prev/next, on garde seulement les dropdowns
        nav: "hidden",

        // Table
        month_grid: "w-full border-collapse",

        // ✅ IMPORTANT : header en grid 7 colonnes (sinon tout se décale)
        weekdays:
          "grid grid-cols-7 bg-neutral-100 px-4 pt-1.5 pb-2 border-b [@media(min-height:700px)]:px-6 [@media(min-height:700px)]:pt-2 [@media(min-height:700px)]:pb-4",
        weekday: "text-center text-[11px] font-medium text-neutral-500 [@media(min-height:700px)]:text-sm",

        // ✅ IMPORTANT : chaque semaine en grid 7 colonnes
        weeks: "block pt-1 pb-1 [@media(min-height:700px)]:pt-2 [@media(min-height:700px)]:pb-2",
        week: "grid grid-cols-7 px-4 py-0.5 [@media(min-height:700px)]:px-6 [@media(min-height:700px)]:py-1.5",

        // Cellules + boutons
        day: "p-0 text-center text-sm",
        // Le survol ne s'allume que sur un jour RÉELLEMENT choisissable : hors
        // bornes, `react-day-picker` pose `disabled` (ou `aria-disabled` quand
        // le jour est focalisé), et `:hover` matche quand même un bouton
        // désactivé — on promettrait un clic que la date refuse.
        day_button:
          "mx-auto flex h-9 w-9 items-center justify-center rounded-md text-[13px] hover:bg-neutral-100 disabled:hover:bg-transparent aria-disabled:hover:bg-transparent focus:outline-none focus:ring-0 [@media(min-height:700px)]:h-11 [@media(min-height:700px)]:w-11 [@media(min-height:700px)]:text-sm",

        // 🔴 `selected` est posé sur la CELLULE (le `<td>`), `day_button` sur le
        // BOUTON qu'elle contient : le `hover:bg-neutral-100` du bouton peint
        // donc PAR-DESSUS le fond de la cellule, pendant que le `text-white`
        // continue d'être hérité. Survoler la date choisie l'affichait en blanc
        // sur gris très clair — le jour DISPARAISSAIT. Un `hover:bg-brand-600`
        // posé sur la cellule (ce qu'il y avait ici) n'y peut structurellement
        // rien : il repeint le calque du DESSOUS. Le survol d'une date
        // sélectionnée s'écrit sur le bouton, ou il ne s'écrit nulle part.
        selected:
          "bg-brand-600 text-white rounded-md [&>button:hover]:bg-brand-700 [&>button:hover]:text-white",
        today: "font-semibold",
        // 🔴 Un jour du mois VOISIN est CHOISISSABLE : il ne doit pas avoir
        // l'air interdit. Il était en `neutral-300` (1,31:1 sur blanc) quand un
        // jour hors bornes est en `neutral-300` à 50 % (1,14:1) — mesuré le
        // 2026-09-30 : indiscernables, et tous deux illisibles. En fin de mois,
        // les premiers jours du mois suivant se lisaient donc « indisponibles »
        // — le défaut signalé sur la grille de réservation (`MonthGrid`), dans
        // sa version champ de date. `neutral-500` (3,06:1) reste en retrait
        // sans mentir.
        // Les deux variantes par attribut (spécificité classe + attribut)
        // l'emportent sur la classe simple QUEL QUE SOIT l'ordre du CSS : un
        // jour voisin CHOISI reste blanc sur la pastille, un jour voisin HORS
        // BORNES reste pâle — sinon la couleur gagnante dépendrait de l'ordre
        // d'émission de Tailwind, pas de l'état du jour.
        outside:
          "text-neutral-500 data-[selected=true]:text-white data-[disabled=true]:text-neutral-300",
        disabled: "text-neutral-300 opacity-50",
        hidden: "invisible",

        ...classNames,
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
